import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { expandibleSwitchStyles } from "./expandible-switch.css";

import "../form-elements/switch/switch";

@customElement("expandible-switch")
export class ExpandibleSwitch extends LitElement {
  @property({ type: Boolean, reflect: true }) isExpanded = false;
  @property({ type: String }) label = "";
  @property({ type: String, attribute: "help-text" }) helpText = "";

  @state() private animatingOut = false;

  static override styles = [expandibleSwitchStyles];

  private async animateContentOut(): Promise<void> {
    const container = this.shadowRoot?.querySelector(".expandible-content") as HTMLElement;
    if (!container) return;

    await container.animate(
      [
        { height: "auto", opacity: 1 },
        { height: "0px", opacity: 0 },
      ],
      {
        duration: 300,
        easing: "ease",
      },
    ).finished;
  }

  private async handleSwitchChange(e: CustomEvent): Promise<void> {
    const newExpanded = e.detail.checked;

    if (this.isExpanded && !newExpanded) {
      // Exit animation first (two-phase pattern)
      this.animatingOut = true;
      await this.animateContentOut();
      this.animatingOut = false;
    }

    this.isExpanded = newExpanded;
    if (this.isExpanded) {
      const isMobileSafari = () => {
        const ua = window.navigator.userAgent;
        return /iP(hone|od|ad)/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
      };

      //it supposed to be in light dom (slotted)
      const input = this.querySelector<HTMLInputElement>("wow-input");
      if (input) {
        if (isMobileSafari()) {
          input.focus();
        } else {
          requestAnimationFrame(() => {
            input.showPicker();
          });
        }
      }
    }

    // Emit change event
    this.dispatchEvent(
      new CustomEvent("expandedChange", {
        detail: { expanded: newExpanded },
        bubbles: true,
        composed: true,
      }),
    );
  }

  protected override render() {
    return html`
      <wow-switch
        label=${this.label}
        help-text=${this.helpText}
        ?checked=${this.isExpanded}
        @change=${this.handleSwitchChange}
      ></wow-switch>

      ${this.isExpanded || this.animatingOut
        ? html`
            <div class="expandible-content">
              <slot name="content"></slot>
            </div>
          `
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "expandible-switch": ExpandibleSwitch;
  }
}
